import { SvgIconComponent } from '@mui/icons-material';
import { merge, startCase } from 'lodash-es';
import { useMemo } from 'react';
import { useLocation } from 'react-router';

import useCurrentPath from '@/hooks/useCurrentPath';
import { ClientDashboardContext } from '@/modules/client/components/pages/ClientDashboard';
import { EnrollmentDashboardContext } from '@/modules/enrollment/components/pages/EnrollmentDashboard';
import { clientBriefName, enrollmentName } from '@/modules/hmis/hmisUtil';
import { ProjectDashboardContext } from '@/modules/projects/components/ProjectDashboard';
import {
  AdminDashboardRoutes,
  ClientDashboardRoutes,
  EnrollmentDashboardRoutes,
  ProjectDashboardRoutes,
  ReferralRoutes,
  Routes,
} from '@/routes/routes';

type CrumbConfig = {
  [x: string]: {
    title: string;
    parent?: string;
    icon?: SvgIconComponent;
  };
};

interface Breadcrumb {
  to: string;
  label: string;
}

const buildParentPaths = (
  currentRoute: string,
  crumbs: string[],
  config: CrumbConfig
) => {
  const parent = config[currentRoute]?.parent;
  if (parent) {
    crumbs.unshift(parent);
    buildParentPaths(parent, crumbs, config);
  }
};

const buildDefaultCrumbs = (
  routes:
    | typeof EnrollmentDashboardRoutes
    | typeof ClientDashboardRoutes
    | typeof ProjectDashboardRoutes
    | typeof AdminDashboardRoutes,
  overrides: CrumbConfig,
  defaultParent?: string
) => {
  const defaults: CrumbConfig = {};
  Object.keys(routes).forEach((key) => {
    const path = routes[key as keyof typeof routes];
    defaults[path] = {
      title: startCase(key.toLowerCase()),
      parent: path === defaultParent ? undefined : defaultParent,
    };
  });
  return merge(defaults, overrides);
};

export const useProjectBreadcrumbConfig = (
  context: ProjectDashboardContext | undefined
): CrumbConfig => {
  return useMemo(() => {
    if (context === undefined) {
      return {};
    }
    const overrides = {
      [ProjectDashboardRoutes.OVERVIEW]: {
        title: context.project.projectName,
      },
      [ProjectDashboardRoutes.PROJECT_ENROLLMENTS]: {
        title: 'Enrollments',
      },
      [ProjectDashboardRoutes.BULK_BED_NIGHTS]: {
        title: 'Bed Nights',
      },
      [ProjectDashboardRoutes.BULK_ASSIGN_SERVICE]: {
        title: 'Bulk Service Assignment',
      },
      [ProjectDashboardRoutes.BULK_SERVICE_NEW_HOUSEHOLD]: {
        title: 'Add Household',
        parent: ProjectDashboardRoutes.BULK_BED_NIGHTS,
      },
      [ProjectDashboardRoutes.BULK_BED_NIGHTS_NEW_HOUSEHOLD]: {
        title: 'Add Household',
        parent: ProjectDashboardRoutes.BULK_BED_NIGHTS,
      },
      [ProjectDashboardRoutes.ADD_HOUSEHOLD]: {
        title: 'Add Enrollment',
        parent: ProjectDashboardRoutes.PROJECT_ENROLLMENTS,
      },
      [ProjectDashboardRoutes.ESG_FUNDING_REPORT]: {
        title: 'ESG Funding Report',
        parent: ProjectDashboardRoutes.REFERRALS,
      },
      // TODO(#8142) remove legacy referral routes
      [ProjectDashboardRoutes.REFERRAL_POSTING]: {
        title: 'Active Referral',
        parent: ProjectDashboardRoutes.REFERRALS,
      },
      [ProjectDashboardRoutes.NEW_FUNDER]: {
        title: 'Add Funder',
        parent: ProjectDashboardRoutes.FUNDERS,
      },
      [ProjectDashboardRoutes.EDIT_FUNDER]: {
        title: 'Edit Funder',
        parent: ProjectDashboardRoutes.FUNDERS,
      },
      [ProjectDashboardRoutes.COCS]: {
        title: 'Project CoCs',
      },
      [ProjectDashboardRoutes.NEW_COC]: {
        title: 'Add CoC',
        parent: ProjectDashboardRoutes.COCS,
      },
      [ProjectDashboardRoutes.EDIT_COC]: {
        title: 'Edit CoC',
        parent: ProjectDashboardRoutes.COCS,
      },
      [ProjectDashboardRoutes.NEW_INVENTORY]: {
        title: 'Add Inventory',
        parent: ProjectDashboardRoutes.INVENTORY,
      },
      [ProjectDashboardRoutes.EDIT_INVENTORY]: {
        title: 'Update Inventory',
        parent: ProjectDashboardRoutes.INVENTORY,
      },
      [ProjectDashboardRoutes.HMIS_PARTICIPATION]: {
        title: 'HMIS Participation',
      },
      [ProjectDashboardRoutes.CE_PARTICIPATION]: {
        title: 'CE Participation',
      },
      [ProjectDashboardRoutes.CE]: {
        title: 'Referrals',
      },
      [ProjectDashboardRoutes.REFERRAL]: {
        title: 'Referral',
        parent: ProjectDashboardRoutes.CE,
      },
      [ProjectDashboardRoutes.REFERRAL_STEP]: {
        title: 'Step',
        parent: ProjectDashboardRoutes.REFERRAL,
      },
      [ProjectDashboardRoutes.SEND_REFERRAL]: {
        title: 'Send Referral',
        parent: ProjectDashboardRoutes.REFERRALS,
      },
      [ProjectDashboardRoutes.UNIT_GROUP]: {
        title: 'Unit Group',
        parent: ProjectDashboardRoutes.UNITS,
      },
      [ProjectDashboardRoutes.UNIT]: {
        title: 'Unit',
        parent: ProjectDashboardRoutes.UNITS,
      },
      [ProjectDashboardRoutes.CE_UNIT]: {
        title: 'Unit',
        parent: ProjectDashboardRoutes.CE,
      },
    };
    const projectRoot = ProjectDashboardRoutes.OVERVIEW;
    return buildDefaultCrumbs(ProjectDashboardRoutes, overrides, projectRoot);
  }, [context]);
};

export const useReferralBreadcrumbConfig = (): CrumbConfig => {
  return useMemo(() => {
    return {
      [Routes.REFERRALS]: { title: 'Referrals' },
      [ReferralRoutes.REFERRAL]: {
        title: 'Referral',
        parent: Routes.REFERRALS,
      },
      [ReferralRoutes.REFERRAL_STEP]: {
        title: 'Step',
        parent: ReferralRoutes.REFERRAL,
      },
    };
  }, []);
};

export const useClientBreadcrumbConfig = (
  context: ClientDashboardContext | undefined
): CrumbConfig => {
  return useMemo(() => {
    if (!context) return {};

    const clientRoot = ClientDashboardRoutes.PROFILE;
    const overrides = {
      [ClientDashboardRoutes.PROFILE]: {
        title: clientBriefName(context.client),
      },
      [ClientDashboardRoutes.EDIT]: {
        title: 'Update Client Details',
      },
      [ClientDashboardRoutes.CLIENT_ENROLLMENTS]: {
        title: 'Enrollments',
      },
      [ClientDashboardRoutes.NEW_ENROLLMENT]: {
        title: 'Add Enrollment',
        parent: ClientDashboardRoutes.CLIENT_ENROLLMENTS,
      },
      [ClientDashboardRoutes.AUDIT_HISTORY]: {
        title: 'Client Audit History',
      },
      [ClientDashboardRoutes.NEW_MERGE]: {
        title: 'New Merge',
        parent: ClientDashboardRoutes.MERGE_HISTORY,
      },
      [ClientDashboardRoutes.NEW_FILE]: {
        title: 'Upload',
        parent: ClientDashboardRoutes.FILES,
      },
      [ClientDashboardRoutes.EDIT_FILE]: {
        title: 'Edit File',
        parent: ClientDashboardRoutes.FILES,
      },
    };
    return buildDefaultCrumbs(ClientDashboardRoutes, overrides, clientRoot);
  }, [context]);
};

export const useEnrollmentBreadcrumbConfig = (
  context: EnrollmentDashboardContext | undefined
): CrumbConfig => {
  return useMemo(() => {
    if (!context) return {};

    const clientRoot = ClientDashboardRoutes.PROFILE;
    const enrollmentRoot = EnrollmentDashboardRoutes.ENROLLMENT_OVERVIEW;
    const enrollmentTitle = context.enrollment
      ? enrollmentName(context.enrollment)
      : 'Enrollment';

    const overrides: CrumbConfig = {
      [clientRoot]: { title: clientBriefName(context.client) },
      [enrollmentRoot]: { parent: clientRoot, title: enrollmentTitle },
      [EnrollmentDashboardRoutes.EDIT_HOUSEHOLD]: { title: 'Edit Household' },
      [EnrollmentDashboardRoutes.CE_ASSESSMENTS]: { title: 'CE Assessments' },
      [EnrollmentDashboardRoutes.EVENTS]: { title: 'CE Events' },
      [EnrollmentDashboardRoutes.CUSTOM_CASE_NOTES]: { title: 'Case Notes' },
      [EnrollmentDashboardRoutes.VIEW_ASSESSMENT]: {
        title: 'Assessment',
        parent: EnrollmentDashboardRoutes.ASSESSMENTS,
      },
      [EnrollmentDashboardRoutes.NEW_ASSESSMENT]: {
        title: 'New Assessment',
        parent: EnrollmentDashboardRoutes.ASSESSMENTS,
      },
    };
    return buildDefaultCrumbs(
      EnrollmentDashboardRoutes,
      overrides,
      enrollmentRoot
    );
  }, [context]);
};

export const useAdminBreadcrumbConfig = (): CrumbConfig => {
  return useMemo(() => {
    // const root = Routes.ADMIN;
    const overrides = {
      [AdminDashboardRoutes.AC_DENIAL_DETAILS]: {
        title: 'Manage Denial',
        parent: AdminDashboardRoutes.AC_DENIALS,
      },
      [AdminDashboardRoutes.AC_DENIALS]: {
        title: 'Denials',
      },
      [AdminDashboardRoutes.CE_RULES]: {
        title: 'Rules',
      },
      [AdminDashboardRoutes.CE_RULE_GLOBAL_DETAIL]: {
        title: 'Rule',
        parent: AdminDashboardRoutes.CE_RULES,
      },
      [AdminDashboardRoutes.CE_RULE_GLOBAL_NEW]: {
        title: 'Add Rule',
        parent: AdminDashboardRoutes.CE_RULES,
      },
      [AdminDashboardRoutes.CE_RULE_ORGANIZATIONS]: {
        title: 'Organizations',
        parent: AdminDashboardRoutes.CE_RULES,
      },
      [AdminDashboardRoutes.CE_RULE_ORGANIZATION]: {
        title: 'Organization', // Overridden in the page with overrideBreadcrumbTitles
        parent: AdminDashboardRoutes.CE_RULE_ORGANIZATIONS,
      },
      [AdminDashboardRoutes.CE_RULE_ORGANIZATION_NEW]: {
        title: 'Add Rule',
        parent: AdminDashboardRoutes.CE_RULE_ORGANIZATION,
      },
      [AdminDashboardRoutes.CE_RULE_ORGANIZATION_DETAIL]: {
        title: 'Rule',
        parent: AdminDashboardRoutes.CE_RULE_ORGANIZATION,
      },
      [AdminDashboardRoutes.CE_RULE_PROJECTS]: {
        title: 'Projects',
        parent: AdminDashboardRoutes.CE_RULES,
      },
      [AdminDashboardRoutes.CE_RULE_PROJECT]: {
        title: 'Project', // Overridden in the page with overrideBreadcrumbTitles
        parent: AdminDashboardRoutes.CE_RULE_PROJECTS,
      },
      [AdminDashboardRoutes.CE_RULE_PROJECT_NEW]: {
        title: 'Add Rule',
        parent: AdminDashboardRoutes.CE_RULE_PROJECT,
      },
      [AdminDashboardRoutes.CE_RULE_PROJECT_DETAIL]: {
        title: 'Rule',
        parent: AdminDashboardRoutes.CE_RULE_PROJECT,
      },
      [AdminDashboardRoutes.CE_RULE_UNIT_GROUPS]: {
        title: 'Unit Groups',
        parent: AdminDashboardRoutes.CE_RULES,
      },
      [AdminDashboardRoutes.CE_RULE_UNIT_GROUP]: {
        title: 'Unit Group', // Overridden in the page with overrideBreadcrumbTitles
        parent: AdminDashboardRoutes.CE_RULE_UNIT_GROUPS,
      },
      [AdminDashboardRoutes.CE_RULE_UNIT_GROUP_NEW]: {
        title: 'Add Rule',
        parent: AdminDashboardRoutes.CE_RULE_UNIT_GROUP,
      },
      [AdminDashboardRoutes.CE_RULE_UNIT_GROUP_DETAIL]: {
        title: 'Rule',
        parent: AdminDashboardRoutes.CE_RULE_UNIT_GROUP,
      },
      [AdminDashboardRoutes.PERFORM_CLIENT_MERGES]: {
        title: 'Potential Duplicates',
        parent: AdminDashboardRoutes.CLIENT_MERGE_HISTORY,
      },
      [AdminDashboardRoutes.CONFIGURE_SERVICES]: {
        title: 'Service Types',
      },
      [AdminDashboardRoutes.CONFIGURE_SERVICE_TYPE]: {
        parent: AdminDashboardRoutes.CONFIGURE_SERVICES,
        title: 'View Service Type',
      },
      [AdminDashboardRoutes.VIEW_FORM]: {
        parent: AdminDashboardRoutes.FORMS,
        title: 'View Form Details',
      },
      [AdminDashboardRoutes.JSON_EDIT_FORM]: {
        parent: AdminDashboardRoutes.EDIT_FORM,
        title: 'Edit Form JSON',
      },
      [AdminDashboardRoutes.EDIT_FORM]: {
        parent: AdminDashboardRoutes.VIEW_FORM,
        title: 'Edit Form',
      },
      [AdminDashboardRoutes.PREVIEW_FORM]: {
        parent: AdminDashboardRoutes.VIEW_FORM,
        title: 'Preview Form',
      },
      [AdminDashboardRoutes.PREVIEW_FORM_DRAFT]: {
        parent: AdminDashboardRoutes.EDIT_FORM,
        title: 'Preview Form',
      },
      [AdminDashboardRoutes.USER_CLIENT_ACCESS_HISTORY]: {
        parent: AdminDashboardRoutes.USERS,
        title: 'Audit User',
      },
      [AdminDashboardRoutes.USER_ENROLLMENT_ACCESS_HISTORY]: {
        parent: AdminDashboardRoutes.USERS,
        title: 'Audit User',
      },
      [AdminDashboardRoutes.USER_EDIT_HISTORY]: {
        parent: AdminDashboardRoutes.USERS,
        title: 'Audit User',
      },
      [AdminDashboardRoutes.USER_LOGIN_ACTIVITY]: {
        parent: AdminDashboardRoutes.USERS,
        title: 'Audit User',
      },
    };
    return buildDefaultCrumbs(AdminDashboardRoutes, overrides);
  }, []);
};

export const useDashboardBreadcrumbs = (
  crumbConfig: CrumbConfig,
  breadcrumbOverrides?: Record<string, string>
): Array<Breadcrumb> => {
  const { pathname } = useLocation();

  const currentPath = useCurrentPath();
  const breadcrumbs = useMemo(() => {
    if (!currentPath) {
      console.warn('route not recognized', pathname);
      return [];
    }

    const paths = [currentPath];
    buildParentPaths(currentPath, paths, crumbConfig);
    const crumbs = paths.map((path) => ({
      to: path,
      label: breadcrumbOverrides?.[path] || crumbConfig[path]?.title || 'Page',
      icon: crumbConfig[path]?.icon,
    }));

    return crumbs;
  }, [pathname, breadcrumbOverrides, crumbConfig, currentPath]);

  return breadcrumbs;
};
